import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    PlayCircleIcon,
    VideoCameraIcon,
    LinkIcon,
    ArrowTopRightOnSquareIcon,
    SignalIcon,
    StopCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/buttons/Button';
import { ExpandableText } from '../../../components/expandableText/ExpandableText';
import { Tooltip } from '../../../components/smartTable/Tooltip';
import { sentenceCase } from '../../../common/upperCaseWord';
import {
    EXTERNAL_MEETING_LINK_ERROR,
    EXTERNAL_MEETING_LINK_HINT,
    isExternalMeetingUrl,
} from '../../../utils/externalMeetingUrl';
import './class-slots-panel.css';

const statusConfig = {
    upcoming: { label: 'En curso', badgeClass: 'class-slots-panel__badge--upcoming' },
    live: { label: 'En vivo', badgeClass: 'class-slots-panel__badge--live', showDot: true },
    past: { label: 'Finalizada', badgeClass: 'class-slots-panel__badge--past' },
};

const cardModifier = (slot, isLatestPast) => {
    if (slot.isTest || slot.source === 'adhoc') return 'class-slots-panel__card--test';
    if (slot.temporalStatus === 'live') return 'class-slots-panel__card--live';
    if (isLatestPast) return 'class-slots-panel__card--highlight';
    if (slot.temporalStatus === 'past') return 'class-slots-panel__card--past';
    return '';
};

const formatCost = (cost) => {
    if (!cost || cost.estimate == null) return null;
    const currency = cost.currency || 'USD';
    try {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(cost.estimate);
    } catch {
        return `${cost.estimate} ${currency}`;
    }
};

export const ClassSlotCard = ({
    slot,
    isLatestPast,
    canCreate,
    onJoin,
    onEndClass,
    onWatch,
    onSaveExternalLink,
    onOpenDiagnostics,
    canSeeDiagnostics = false,
    loading,
    offlineMode = false,
    showCost = false,
    joinedSessionId = null,
}) => {
    const [linkDraft, setLinkDraft] = useState(slot.externalLink || slot.session?.externalLink || '');
    const [linkEditing, setLinkEditing] = useState(false);
    const [linkError, setLinkError] = useState('');
    const [savingLink, setSavingLink] = useState(false);

    useEffect(() => {
        setLinkDraft(slot.externalLink || slot.session?.externalLink || '');
    }, [slot.externalLink, slot.session?.externalLink]);

    const hasSession = Boolean(slot.session);
    const sessionStatus = slot.session?.status;
    const sessionIsLive = Boolean(slot.hasLiveZoom) || sessionStatus === 'live';
    const classOpen = hasSession && !['ended', 'cancelled'].includes(sessionStatus);
    const resolvedTemporalStatus = sessionIsLive
        ? 'live'
        : (classOpen ? 'upcoming' : 'past');
    const status = statusConfig[resolvedTemporalStatus] || statusConfig.upcoming;

    const canViewDiagnostics = canSeeDiagnostics && Boolean(slot.session?._id);
    const canStartZoom = !offlineMode && canCreate && classOpen && !sessionIsLive;
    const alreadyInThisCall = Boolean(joinedSessionId)
        && String(joinedSessionId) === String(slot.session?._id);
    const canRejoinZoom = !offlineMode && canCreate && sessionIsLive && !alreadyInThisCall;
    const canJoinAsStudent = !offlineMode && !canCreate && sessionIsLive && !alreadyInThisCall;
    const canEndClass = canCreate && classOpen;

    const readyRecordings = (slot.recordings || []).filter((r) => r?.available && r._id);
    const canWatch = !offlineMode && readyRecordings.length > 0;
    const recordingProcessing = !offlineMode
        && hasSession
        && !sessionIsLive
        && (
            slot.recordings?.some((r) => r.pending && !r.available)
            || slot.zoomCalls?.some((c) => c.recordingPending && !c.recordingReady)
            || ((slot.session?.recordingPending || slot.recording?.pending) && !slot.session?.recordingReady && readyRecordings.length === 0)
        );

    const externalLink = slot.externalLink || slot.session?.externalLink || '';
    const canEditLink = offlineMode && canCreate && hasSession && sessionStatus !== 'cancelled';
    const canOpenExternal = Boolean(externalLink) && offlineMode && sessionStatus !== 'cancelled';

    const startDate = new Date(slot.scheduledStart);
    const dayNumber = format(startDate, 'd', { locale: es });
    const monthLabel = format(startDate, 'MMM', { locale: es }).replace('.', '');

    const displayLive = sessionIsLive;
    const modifier = displayLive
        ? 'class-slots-panel__card--live'
        : cardModifier(slot, isLatestPast);
    const hasAction = canStartZoom
        || canRejoinZoom
        || canJoinAsStudent
        || canEndClass
        || canWatch
        || recordingProcessing
        || canOpenExternal
        || canEditLink
        || canViewDiagnostics;

    const statusBadgeClass = displayLive
        ? 'class-slots-panel__badge--live'
        : status.badgeClass;
    const statusLabel = displayLive
        ? 'En vivo'
        : status.label;
    const showStatusDot = displayLive;

    const durationLabel = classOpen
        ? null
        : (slot.durationMinutes || slot.session?.durationMinutes);
    const costLabel = showCost ? formatCost(slot.cost || slot.session?.cost) : null;
    const description = slot.description || slot.session?.description || '';

    const handleSaveLink = async () => {
        const next = linkDraft.trim();
        if (next && !isExternalMeetingUrl(next)) {
            setLinkError(EXTERNAL_MEETING_LINK_ERROR);
            return;
        }
        setLinkError('');
        setSavingLink(true);
        try {
            const ok = await onSaveExternalLink?.(slot, next);
            if (ok) setLinkEditing(false);
            else if (next) setLinkError(EXTERNAL_MEETING_LINK_ERROR);
        } catch (err) {
            setLinkError(err?.message || err?.response?.data?.message || EXTERNAL_MEETING_LINK_ERROR);
        } finally {
            setSavingLink(false);
        }
    };

    return (
        <article className={`class-slots-panel__card${modifier ? ` ${modifier}` : ''}`}>
            <div className="class-slots-panel__date-stamp" aria-hidden="true">
                <span className="class-slots-panel__date-month">{monthLabel}</span>
                <span className="class-slots-panel__date-day">{dayNumber}</span>
            </div>

            <div className="class-slots-panel__card-main">
                <div className="class-slots-panel__card-body">
                    <h3
                        className="class-slots-panel__card-title"
                        title={sentenceCase(slot.topic || slot.session?.topic || slot.dayLabel)}
                    >
                        {sentenceCase(slot.topic || slot.session?.topic || slot.dayLabel)}
                    </h3>

                    <div className="class-slots-panel__meta-row">
                        <p className="class-slots-panel__card-time">
                            {slot.timeSlot}
                            {durationLabel ? ` · ${durationLabel} min` : ''}
                            {costLabel ? ` · Est. ${costLabel}` : ''}
                        </p>
                        <div className="class-slots-panel__badges">
                            <span className={`class-slots-panel__badge ${statusBadgeClass}`}>
                                {showStatusDot ? (
                                    <span className="class-slots-panel__live-dot" aria-hidden="true" />
                                ) : null}
                                {statusLabel}
                            </span>
                            {hasSession && sessionStatus === 'created' && !offlineMode && (
                                <span className="class-slots-panel__badge class-slots-panel__badge--created">
                                    Lista
                                </span>
                            )}
                            {canWatch && (
                                <span className="class-slots-panel__badge class-slots-panel__badge--registered">
                                    {readyRecordings.length > 1
                                        ? `${readyRecordings.length} grabaciones`
                                        : 'Grabación'}
                                </span>
                            )}
                            {recordingProcessing && (
                                <span className="class-slots-panel__badge class-slots-panel__badge--upcoming">
                                    Procesando…
                                </span>
                            )}
                            {externalLink && (
                                <span className="class-slots-panel__badge class-slots-panel__badge--created">
                                    Link
                                </span>
                            )}
                        </div>
                    </div>

                    {description ? <ExpandableText text={description} /> : null}
                </div>

                {hasAction && !linkEditing ? (
                    <div className="class-slots-panel__card-footer">
                        <div className="class-slots-panel__actions">
                            {(canStartZoom || canRejoinZoom || canJoinAsStudent || canEndClass) ? (
                                <div className="class-slots-panel__actions-group">
                                    {canStartZoom && (
                                        <Tooltip title="Abrir la videollamada de esta clase">
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="sm"
                                                className="class-slots-panel__action-btn class-slots-panel__action-btn--start"
                                                icon={<VideoCameraIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                                                loading={loading}
                                                onClick={() => onJoin(slot)}
                                            >
                                                Iniciar Zoom
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {(canRejoinZoom || canJoinAsStudent) && (
                                        <Tooltip title="Entrar a la videollamada en curso">
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="sm"
                                                className="class-slots-panel__action-btn class-slots-panel__action-btn--start"
                                                icon={<VideoCameraIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                                                loading={loading}
                                                onClick={() => onJoin(slot)}
                                            >
                                                Unirse
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {canEndClass && (
                                        <Tooltip title="Cierra la clase para todos los participantes">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="class-slots-panel__action-btn class-slots-panel__action-btn--end"
                                                icon={<StopCircleIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                                                loading={loading}
                                                onClick={() => onEndClass?.(slot)}
                                            >
                                                Terminar clase
                                            </Button>
                                        </Tooltip>
                                    )}
                                </div>
                            ) : null}

                            {(canOpenExternal || canEditLink || canViewDiagnostics
                                || readyRecordings.length > 0) ? (
                                <div className="class-slots-panel__actions-group">
                                    {readyRecordings.map((recording, index) => (
                                        <Tooltip
                                            key={String(recording._id)}
                                            title="Ver la grabación de esta clase"
                                        >
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="class-slots-panel__action-btn"
                                                icon={<PlayCircleIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                                                loading={loading}
                                                onClick={() => onWatch(slot, recording)}
                                            >
                                                {readyRecordings.length > 1
                                                    ? `Grabación ${index + 1}`
                                                    : 'Ver clase'}
                                                {recording.duration ? ` · ${recording.duration} min` : ''}
                                            </Button>
                                        </Tooltip>
                                    ))}
                                    {canOpenExternal && (
                                        <Tooltip title="Abrir el link de la clase en otra pestaña">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="class-slots-panel__action-btn class-slots-panel__action-btn--link-open"
                                                icon={<ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                                                onClick={() => window.open(externalLink, '_blank', 'noopener,noreferrer')}
                                            >
                                                Abrir link
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {canEditLink && (
                                        <Tooltip title={externalLink ? 'Cambiar el link de la clase' : 'Pegar el link de Meet, Zoom, Teams o Webex'}>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="class-slots-panel__action-btn class-slots-panel__action-btn--link-edit"
                                                icon={<LinkIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                                                loading={loading}
                                                onClick={() => setLinkEditing(true)}
                                            >
                                                {externalLink ? 'Editar' : 'Ligar link'}
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {canViewDiagnostics && (
                                        <Tooltip title="Diagnóstico de conexión: caídas y reconexiones">
                                            <button
                                                type="button"
                                                className="class-slots-panel__icon-btn"
                                                aria-label="Ver diagnóstico de conexión"
                                                onClick={() => onOpenDiagnostics?.(slot)}
                                            >
                                                <SignalIcon aria-hidden="true" />
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {canEditLink && linkEditing ? (
                    <div className="class-slots-panel__link-editor">
                        <div className="class-slots-panel__link-field">
                            <input
                                id={`class-external-link-${slot.session?._id || slot.slotKey}`}
                                name="externalLink"
                                type="url"
                                inputMode="url"
                                autoComplete="off"
                                spellCheck={false}
                                aria-label="Link de la clase"
                                aria-invalid={linkError ? true : undefined}
                                aria-describedby={`class-external-link-${linkError ? 'error' : 'hint'}-${slot.session?._id || slot.slotKey}`}
                                className={`class-slots-panel__link-input${linkError ? ' is-invalid' : ''}`}
                                placeholder="Pega aquí el link de la clase"
                                value={linkDraft}
                                disabled={savingLink || loading}
                                onChange={(e) => {
                                    setLinkDraft(e.target.value);
                                    if (linkError) setLinkError('');
                                }}
                            />
                            {linkError ? (
                                <p
                                    id={`class-external-link-error-${slot.session?._id || slot.slotKey}`}
                                    className="class-slots-panel__link-error"
                                    role="alert"
                                >
                                    {linkError}
                                </p>
                            ) : (
                                <p
                                    id={`class-external-link-hint-${slot.session?._id || slot.slotKey}`}
                                    className="class-slots-panel__link-hint"
                                >
                                    {EXTERNAL_MEETING_LINK_HINT}
                                </p>
                            )}
                        </div>
                        <div className="class-slots-panel__link-actions">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="class-slots-panel__action-btn"
                                disabled={savingLink || loading}
                                onClick={() => {
                                    setLinkDraft(externalLink);
                                    setLinkError('');
                                    setLinkEditing(false);
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="button"
                                variant="gradient"
                                size="sm"
                                className="class-slots-panel__action-btn"
                                loading={savingLink}
                                loadingLabel="Guardando…"
                                onClick={handleSaveLink}
                            >
                                Guardar
                            </Button>
                        </div>
                    </div>
                ) : null}

                {recordingProcessing ? (
                    <p className="class-slots-panel__processing-hint">
                        Zoom está procesando el vídeo (suele tardar ~el doble de lo grabado).
                    </p>
                ) : null}
            </div>
        </article>
    );
};
